import { useState, useRef, useEffect, useCallback } from 'react'
import { lib } from '@gocanvas/client'
const { isNumber } = lib


// no validation, cancelable
export const useSimpleInactiveUpdate = (initialValue, onChange, delay = 500) => {
  // value rendered from, say, state, normalized as a string
  const cleanInitialValue = initialValue === null || initialValue === undefined ? '' : String(initialValue)
  const [inputValue, setInputValue] = useState(cleanInitialValue)
  const timeoutIdRef = useRef(null)
  const liveValue = useRef(inputValue)

  // make sure when the delay runs, it always has the updated value to compare
  useEffect(() => {
    if (cleanInitialValue !== inputValue) {
      setInputValue(cleanInitialValue)
      liveValue.current = cleanInitialValue
    }
  }, [cleanInitialValue])

  const cancelUpdate = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }
  }

  // direct change
  const handleChange = value => {
    if (timeoutIdRef.current) {
      cancelUpdate()
    }
    setInputValue(value)
    liveValue.current = value
  }

  // blurs immediately updates the value and cancel this update
  const handleBlur = () => {
    if (cleanInitialValue != liveValue.current && timeoutIdRef.current) {
      cancelUpdate()
      onChange(liveValue.current)
    }
  }

  // update is scheduled after a delay if there's a mismatch
  useEffect(() => {
    if (cleanInitialValue != inputValue) {
      timeoutIdRef.current = setTimeout(() => {
        onChange(inputValue)
      }, delay)
    }
    return () => clearTimeout(timeoutIdRef.current)
  }, [inputValue, delay, cleanInitialValue])

  return {
    cancelUpdate,
    inputValue,
    handleChange,
    handleBlur,
  }
}

export const useInactiveUpdate = (
  initialValue,
  validate,
  onChange,
  {
    delay = 500,
    min,
    max,
  }) => {
  const cleanInitialValue = initialValue === null || initialValue === undefined ? '' : String(initialValue)
  const [inputValue, setInputValue] = useState(cleanInitialValue)
  const [errors, setErrors] = useState(null)
  const lastValidValue = useRef(inputValue)
  const timeoutIdRef = useRef(null)

  const validateInput = useCallback(value => {
    if (validate) {
      const { errors = null } = validate(value)
      setErrors(errors)
      return !errors
    }
    return true
  }, [validate])

  // updates the visual component
  // stores last change as valid only if it's valid
  const handleChange = value => {
    setInputValue(value)

    if (validateInput(value)) {
      lastValidValue.current = value
    }
    // if min or max exist, clamp the attempted value
    else if (isNumber(min) && Number(value) < Number(min)) {
      lastValidValue.current = min
    }
    else if (isNumber(max) && Number(value) > Number(max)) {
      lastValidValue.current = max
    }
  }

  useEffect(() => {
    if (cleanInitialValue != inputValue && validateInput(cleanInitialValue )) {
      setInputValue(cleanInitialValue)
      lastValidValue.current = cleanInitialValue 
    }
  }, [cleanInitialValue])

  const handleBlur = () => {
    if (!errors && cleanInitialValue !== lastValidValue.current) {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      onChange(lastValidValue.current)
    } else {
      setInputValue(lastValidValue.current)
      setErrors(null)
    }
  }

  const handleKeyUp = ({ key }) => {
    if (key === 'Enter' && !errors && cleanInitialValue !== lastValidValue.current) {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      onChange(lastValidValue.current)
    }
  }

  useEffect(() => {
    if (!errors && cleanInitialValue !== lastValidValue.current) {
      timeoutIdRef.current = setTimeout(() => {
        onChange(lastValidValue.current)
      }, delay)
    }
    return () => clearTimeout(timeoutIdRef.current)
  }, [inputValue, errors, delay, cleanInitialValue])

  return {
    inputValue,
    errors,
    handleChange,
    handleBlur,
    handleKeyUp,
  }
}
